/**
 * venues.js — the exchanges the ingestor feeds, in one place.
 *
 * Radar, the Screener's coin list and the Impulse feed all need the same list
 * with the same colours and labels; three copies would drift the first time a
 * venue is added. `id` matches the Go scanner's venue name and the suffix in the
 * `global_update_<id>` frames it publishes.
 */
/**
 * Exchanges that are live on the site. Owner 10.10.2026: the whole bots plan
 * runs on Binance first (docs/BOTS_PRODUCT_PLAN.md); Bybit / OKX / Bitget are
 * "In development" — their chips stay visible but cannot be picked, and none
 * of their instrument lists, tickers or radar channels are loaded. The
 * ingestor stopped feeding them the same day (ENABLE_BYBIT / ENABLE_OKX). To
 * bring one back: add its id here AND turn its ingestor flag on.
 */
export const LIVE_EXCHANGE_IDS = ['binance'];
export const isExchangeLive = (id) => LIVE_EXCHANGE_IDS.includes(id);

const VENUE_ROWS = [
  { id: 'futures',     exchange: 'binance', label: 'Binance FUT',  short: 'BIN·F', c: '#f0b90b' },
  { id: 'spot',        exchange: 'binance', label: 'Binance SPOT', short: 'BIN·S', c: '#f0b90b' },
  { id: 'bybit',       exchange: 'bybit',   label: 'Bybit FUT',    short: 'BYB·F', c: '#f7a600' },
  { id: 'bybit_spot',  exchange: 'bybit',   label: 'Bybit SPOT',   short: 'BYB·S', c: '#f7a600' },
  { id: 'okx',         exchange: 'okx',     label: 'OKX FUT',      short: 'OKX·F', c: 'var(--violet)' },
  { id: 'okx_spot',    exchange: 'okx',     label: 'OKX SPOT',     short: 'OKX·S', c: 'var(--violet)' },
  { id: 'bitget',      exchange: 'bitget',  label: 'Bitget FUT',   short: 'BG·F',  c: 'var(--info)' },
  { id: 'bitget_spot', exchange: 'bitget',  label: 'Bitget SPOT',  short: 'BG·S',  c: 'var(--info)' },
];
export const VENUES = VENUE_ROWS.map((v) => ({ ...v, live: isExchangeLive(v.exchange) }));
/** The venues whose data actually flows — what filters default to and loaders read. */
export const LIVE_VENUES = VENUES.filter((v) => v.live);

/** One chip per EXCHANGE (both markets folded together) — what the Screener and
 *  the Impulse feed offer, since a chart is per-coin, not per-market. */
export const EXCHANGES = [
  { id: 'binance', label: 'Binance', c: '#f0b90b' },
  { id: 'bybit',   label: 'Bybit',   c: '#f7a600' },
  { id: 'okx',     label: 'OKX',     c: 'var(--violet)' },
  { id: 'bitget',  label: 'Bitget',  c: 'var(--info)' },
].map((e) => ({ ...e, live: isExchangeLive(e.id) }));

/** Every exchange a user can actually select — "all selected" means these. */
export const ALL_EXCHANGE_IDS = EXCHANGES.filter((e) => e.live).map((e) => e.id);

/**
 * A saved exchange/venue selection (localStorage) can still hold a venue that
 * has since gone "In development". Drop those; an empty result falls back to
 * the full live list rather than to a filter that shows nothing.
 */
export function liveSelection(saved, allLiveIds, idToExchange = (id) => id) {
  const kept = Array.isArray(saved) ? saved.filter((id) => isExchangeLive(idToExchange(id))) : [];
  return kept.length ? kept : allLiveIds;
}

export const venueById = (id) => VENUES.find((v) => v.id === id) || null;
/** Tooltip for a venue chip that is shown but not selectable. */
export const IN_DEVELOPMENT = 'In development';
export const exchangeOf = (venueId) => venueById(venueId)?.exchange || 'binance';

/* ─── Pair filtering ────────────────────────────────────────────────────────
 * Lives here rather than in a hook: it is pure data used by charts, tables and
 * the ticker feed alike, none of which should have to import React to use it.
 *
 * SEPARATE LIST from backend-node/src/lib/stablecoins.js on purpose — this is
 * frontend code (a Vite/browser bundle) and cannot import a Node backend
 * module without a shared-package refactor nobody has asked for. But they
 * answer the SAME question ("is this a $1-pegged asset, not a real coin") for
 * different consumers (this one: chart/venue universe; that one: which
 * detectors are allowed to publish a situation on it) — found live 20.08.2026
 * that they had already diverged (this list was missing EURC/USDX/CUSD/
 * CRVUSD/GHO/USDY, that one was missing EURI/AEUR/XUSD/RLUSD/USDG/EURS),
 * synced both directions here. If you add a base to ONE of these two files,
 * add it to the other too — see that file's own matching comment.
 */
const STABLE_BASES = new Set([
  'USDC', 'USDT', 'BUSD', 'FDUSD', 'TUSD', 'USDP', 'USDD', 'DAI', 'USDE',
  'USD1', 'PYUSD', 'EURI', 'AEUR', 'USDS', 'LUSD', 'GUSD', 'FRAX', 'SUSD',
  'USTC', 'XUSD', 'RLUSD', 'USDG', 'EURT', 'EURS',
  // Added 20.08.2026 to match backend-node/src/lib/stablecoins.js:
  'EURC', 'USDX', 'CUSD', 'CRVUSD', 'GHO', 'USDY',
]);
const QUOTES = ['USDT', 'USDC', 'BUSD', 'FDUSD', 'TUSD', 'USD'];

// Permanently hidden tickers — not stablecoins, just coins the owner never wants
// surfaced. Separate set so the reason for each exclusion stays readable.
const EXCLUDED_BASES = new Set(['U', 'BFUSD']);

/** true when the pair is dollar↔dollar OR the base is permanently excluded. */
export function isStablePair(symbol) {
  const s = String(symbol || '').toUpperCase();
  for (const q of QUOTES) {
    if (s.endsWith(q) && s.length > q.length) {
      const base = s.slice(0, -q.length);
      return STABLE_BASES.has(base) || EXCLUDED_BASES.has(base);
    }
  }
  return false;
}
